import { createHttpLink, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { getOrganizationId } from '@lib/graphql/variables/organizationId'
import { FragmentDefinitionNode, OperationDefinitionNode } from 'graphql'
import { createClient } from 'graphql-ws'
import { ApolloConfig } from './apollo'
import { GraphqlApi } from './constants'

const constructBearerToken = (token: string) => `Bearer ${token}`


const isSubscriptionOperation = (
  definition: OperationDefinitionNode | FragmentDefinitionNode
) =>
  definition.kind === 'OperationDefinition' &&
  definition.operation === 'subscription'

const havelockHttpLink = createHttpLink({
  credentials: 'same-origin',
  uri: process.env.NEXT_PUBLIC_API_URL,
})

const prodactiveHttpLink = createHttpLink({
  credentials: 'same-origin',
  uri: process.env.NEXT_PUBLIC_PRODACTIVE_API_URL,
})

const sessionId = crypto.randomUUID()

export const httpLink = split(
  (operation) => operation.getContext().api === GraphqlApi.Prodactive,
  prodactiveHttpLink,
  havelockHttpLink
)

const havelockConnectionParams = async (
  getAccessToken: () => Promise<string | undefined>
) => {
  const token = await getAccessToken()
  const authorizationHeader = token ? constructBearerToken(token) : undefined
  const orgId = getOrganizationId()

  return {
    Authorization: authorizationHeader,
    ...(orgId ? { 'X-Organization-ID': orgId } : {}),
    'X-Client-Version': 'Web 1',
    'X-Session-ID': sessionId,
  }
}

const prodactiveConnectionParams = async (
  getAccessToken: () => Promise<string | undefined>
) => {
  const token = await getAccessToken()
  const orgId = getOrganizationId()

  return {
    authToken: token,
    'X-Organization-ID': orgId,
  }
}

const createWsLink = ({
  url,
  connectionParams,
  triggerReconnection,
}: {
  url: string
  connectionParams: () => Promise<Record<string, unknown>>
  triggerReconnection?: Function
}) => {
  let activeSocket
  let timedOut

  return new GraphQLWsLink(
    createClient({
      url,
      connectionParams,
      keepAlive: 50_000,
      shouldRetry: () => true,
      retryAttempts: Infinity,
      on: {
        connected: (socket) => {
          activeSocket = socket

          triggerReconnection?.()
        },
        ping: (received) => {
          if (!received)
            timedOut = setTimeout(() => {
              if (activeSocket.readyState === WebSocket.OPEN)
                activeSocket.close(4408, 'Request Timeout')
            }, 5_000)
        },
        pong: (received) => {
          if (received) clearTimeout(timedOut)
        },
      },
    })
  )
}

export const createSplitLink = ({
  triggerReconnection,
  authConfig,
}: ApolloConfig) => {
  const { useAuth = true, getHavelockAccessToken, getProdactiveAccessToken } =
    authConfig

  const getToken = (api: GraphqlApi) => async () => {
    if (!useAuth) return undefined
    return api === GraphqlApi.Prodactive
      ? await getProdactiveAccessToken?.()
      : await getHavelockAccessToken?.()
  }

  const havelockWsUrl = process.env.NEXT_PUBLIC_WS_URL as string
  const prodactiveWsUrl = process.env.NEXT_PUBLIC_PRODACTIVE_WS_URL

  const havelockWsLink = createWsLink({
    url: havelockWsUrl,
    connectionParams: () => havelockConnectionParams(getToken(GraphqlApi.Havelock)),
    triggerReconnection,
  })

  let wsLink
  if (prodactiveWsUrl) {
    const prodactiveWsLink = createWsLink({
      url: prodactiveWsUrl,
      connectionParams: () => prodactiveConnectionParams(getToken(GraphqlApi.Prodactive)),
      triggerReconnection,
    })

    wsLink = split(
      (operation) => operation.getContext().api === GraphqlApi.Prodactive,
      prodactiveWsLink,
      havelockWsLink
    )
  } else {
    wsLink = havelockWsLink
  }

  return split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return isSubscriptionOperation(definition)
    },
    wsLink,
    httpLink
  )
}
export const cleanTypeNameLink = removeTypenameFromVariables();

export const createAuthLink = ({
  defaultApiContext,
  authConfig,
}: ApolloConfig) => {
  return setContext(async (_, { headers, api }) => {
    api = api || defaultApiContext
    const {
      useAuth = true,
      getHavelockAccessToken,
      getProdactiveAccessToken,
    } = authConfig

    const authorizationHeader = useAuth
      ? constructBearerToken(
          api === GraphqlApi.Prodactive
            ? await getProdactiveAccessToken?.()
            : await getHavelockAccessToken?.()
        )
      : undefined

    const orgId = getOrganizationId()

    const additionalHeaders = authorizationHeader
      ? {
          Authorization: authorizationHeader,
        }
      : {}

    if (orgId) {
      additionalHeaders['X-Organization-ID'] = orgId
    }

    return {
      headers: {
        ...headers,
        ...additionalHeaders,
        'X-Client-Version': `Web 1`,
        'X-Session-ID': sessionId,
      },
      api,
    }
  })
}
